<template>
    <Notification
        v-if="requirement"
        variant="warning"
        icon="InfoCircle"
        padding="l"
        :tier="2">
        {{ requirement.label }}
        <ButtonSecondary size="s" :onClick="requirement.onClick">
            {{ t({ key: 'shared.add_now' }) }}
        </ButtonSecondary>
    </Notification>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { defineComponent, computed, PropType } from 'vue';
    import { Customer, TransferMainPassenger } from '@/api/Cart/cart.interfaces';
    import { useStore } from '@/store';
    import { scrollToElementByAttribute } from '@shared/helpers/scroll';
    import { CheckoutStepType } from '@/store/checkout/types';
    import { getSectionProductId } from '@/components/checkoutPageV3/CheckoutV3.helpers';
    import { getMainPassenger, getValidTransferProductId } from '@/helpers/transfer/transfer';
    import { TransferTrip, AnyTransferProduct } from '@/helpers/transfer/transfer.interfaces';
    import Notification from '@ui/components/Notification/Notification.vue';
    import ButtonSecondary from '@ui/components/Button/ButtonSecondary.vue';

    interface Requirement {
        label: string;
        onClick: () => void;
    }

    export default defineComponent({
        name: 'TransferRequirement',
        methods: { t },
        components: {
            Notification,
            ButtonSecondary,
        },
        props: {
            trip: { type: Object as PropType<TransferTrip>, required: true },
            onScroll: Function,
        },
        setup(props) {
            const store = useStore();

            const customer = computed<Customer>(() => store.getters['cart/getCustomer']);

            const product = computed<AnyTransferProduct | undefined>(() => {
                const validProductId = getValidTransferProductId(props.trip);
                return store.getters['cart/getProduct'](validProductId);
            });

            const requirement = computed<Requirement | undefined>(() => {
                if (!product.value) return undefined;

                const mainPassenger: TransferMainPassenger | null = getMainPassenger(
                    product.value,
                    customer.value
                );

                if (!mainPassenger) {
                    if (!customer.value.PhoneNumber || !customer.value.Email) {
                        return {
                            label: t({
                                key: 'shared.transfer_more_details_required',
                            }),
                            onClick: () => scrollToSection('Contact'),
                        };
                    }

                    return {
                        label: t({
                            key: 'shared.transfer_more_details_required',
                        }),
                        onClick: () => scrollToSection('Product'),
                    };
                }

                return undefined;
            });

            function scrollToSection(type: 'Contact' | 'Product') {
                let scrollTarget: string | undefined;
                switch (type) {
                    case 'Contact': {
                        scrollTarget = CheckoutStepType.Contact;
                        break;
                    }
                    case 'Product': {
                        if (product.value) {
                            scrollTarget = getSectionProductId(
                                product.value.Id,
                                product.value.Type
                            );
                        }
                        break;
                    }
                }

                if (scrollTarget) {
                    requestAnimationFrame(() => {
                        scrollToElementByAttribute('data-section', scrollTarget!);
                    });
                }

                props.onScroll?.();
            }

            return {
                requirement,
            };
        },
    });
</script>
